import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createGarage,
  deleteGarage,
  fetchGarages,
  resetGaragePassword,
  updateGarage,
} from "../api/garages";
import { logoutAdmin } from "../api/auth";
import { fetchContactRequests } from "../api/contactRequests";
import { fetchMarketplaceUsers } from "../api/marketplaceUsers";

const C = {
  red: "#c1392a",
  navy: "#1a1f2e",
  bg: "#f5f6f8",
  border: "#e2e8f0",
  text: "#1a1f2e",
  muted: "#64748b",
  green: "#15803d",
  amber: "#b45309",
};
const EMPTY = {
  name: "",
  code: "",
  password: "",
  status: "Active",
  subscriptionPlan: "Basic",
  subscriptionStatus: "Trialing",
};

function Badge({ children, tone = "neutral" }) {
  const colors = {
    green: ["#dcfce7", C.green],
    amber: ["#fef3c7", C.amber],
    neutral: ["#f1f5f9", C.muted],
  };
  const [background, color] = colors[tone] || colors.neutral;
  return (
    <span
      style={{
        background,
        color,
        borderRadius: 20,
        padding: "4px 10px",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label
      style={{
        display: "grid",
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: C.muted,
      }}
    >
      {label}
      {children}
    </label>
  );
}

function GarageForm({ value, onChange, onSubmit, onCancel, editing, saving }) {
  const set = (key) => (event) =>
    onChange({ ...value, [key]: event.target.value });
  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Garage name">
          <input required value={value.name} onChange={set("name")} />
        </Field>
        <Field label="Garage code">
          <input
            required
            value={value.code}
            onChange={set("code")}
            disabled={editing}
          />
        </Field>
        {!editing && (
          <Field label="Garage password">
            <input
              required
              minLength={8}
              type="password"
              value={value.password}
              onChange={set("password")}
            />
          </Field>
        )}
        <Field label="Status">
          <select value={value.status} onChange={set("status")}>
            <option>Active</option>
            <option>Suspended</option>
          </select>
        </Field>
        <Field label="Subscription plan">
          <input
            value={value.subscriptionPlan}
            onChange={set("subscriptionPlan")}
            placeholder="Basic or Pro"
          />
        </Field>
        <Field label="Subscription status">
          <select
            value={value.subscriptionStatus}
            onChange={set("subscriptionStatus")}
          >
            <option>Trialing</option>
            <option>Active</option>
            <option>PastDue</option>
            <option>Cancelled</option>
          </select>
        </Field>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          onClick={onCancel}
          style={buttonStyle("secondary")}
        >
          Cancel
        </button>
        <button disabled={saving} style={buttonStyle("primary")}>
          {saving ? "Saving..." : editing ? "Save changes" : "Create garage"}
        </button>
      </div>
    </form>
  );
}

const inputStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: 7,
  padding: "10px 11px",
  fontSize: 13,
  color: C.text,
  width: "100%",
  boxSizing: "border-box",
  background: "#fff",
};
const buttonStyle = (kind) => ({
  border: kind === "primary" ? "none" : `1px solid ${C.border}`,
  borderRadius: 7,
  padding: "9px 14px",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  background: kind === "primary" ? C.red : "#fff",
  color: kind === "primary" ? "#fff" : C.muted,
});

export default function AdminManagement() {
  const navigate = useNavigate();
  const [garages, setGarages] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [passwordGarage, setPasswordGarage] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [contactRequests, setContactRequests] = useState([]);
  const [marketplaceUsers, setMarketplaceUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [garageRows, requests, users] = await Promise.all([
        fetchGarages(),
        fetchContactRequests(),
        fetchMarketplaceUsers(),
      ]);
      setGarages(garageRows);
      setContactRequests(requests);
      setMarketplaceUsers(users);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { password, ...data } = form;
      const result = form.id
        ? await updateGarage(form.id, data)
        : await createGarage(form);
      setGarages((rows) =>
        form.id
          ? rows.map((row) => (row.id === result.id ? result : row))
          : [result, ...rows],
      );
      setForm(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function changePassword(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await resetGaragePassword(passwordGarage.id, newPassword);
      setPasswordGarage(null);
      setNewPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeGarage(garage) {
    if (
      !window.confirm(
        `Delete ${garage.name}? This also deletes its garage records.`,
      )
    )
      return;
    try {
      await deleteGarage(garage.id);
      setGarages((rows) => rows.filter((row) => row.id !== garage.id));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "'Inter',-apple-system,sans-serif",
      }}
    >
      <header
        style={{
          height: 64,
          background: C.navy,
          color: "#fff",
          padding: "0 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <strong>My Car Kenya</strong>
          <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 3 }}>
            Super admin · Garage network
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => setForm(EMPTY)} style={buttonStyle("primary")}>
            Add garage
          </button>
          <button
            onClick={() => {
              logoutAdmin();
              navigate("/admin-login", { replace: true });
            }}
            style={buttonStyle("secondary")}
          >
            Sign out
          </button>
        </div>
      </header>
      <section
        style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 24px" }}
      >
        <h1 style={{ margin: 0, fontSize: 26 }}>Garages</h1>
        <p style={{ margin: "7px 0 24px", color: C.muted, fontSize: 13 }}>
          Create and manage garage accounts, access status, and subscriptions.
        </p>
        {error && (
          <p
            role="alert"
            style={{
              background: "#fef2f2",
              borderLeft: `3px solid ${C.red}`,
              padding: "10px 12px",
              color: C.red,
              fontSize: 13,
            }}
          >
            {error}
          </p>
        )}
        {form && (
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              padding: 20,
              marginBottom: 20,
            }}
          >
            <h2 style={{ fontSize: 16, margin: "0 0 18px" }}>
              {form.id ? "Edit garage" : "Create garage"}
            </h2>
            <GarageForm
              value={form}
              onChange={setForm}
              onSubmit={save}
              onCancel={() => setForm(null)}
              editing={Boolean(form.id)}
              saving={saving}
            />
          </div>
        )}
        {loading ? (
          <p>Loading garages...</p>
        ) : !garages.length ? (
          <p style={{ color: C.muted }}>No garages have been created yet.</p>
        ) : (
          <div
            style={{
              background: "#fff",
              border: `1px solid ${C.border}`,
              borderRadius: 10,
              overflowX: "auto",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 760,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Garage",
                    "Code",
                    "Status",
                    "Plan",
                    "Subscription",
                    "Usage",
                    "Actions",
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        textAlign: "left",
                        padding: "14px 16px",
                        borderBottom: `1px solid ${C.border}`,
                        color: C.muted,
                        fontSize: 10,
                        textTransform: "uppercase",
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {garages.map((garage) => (
                  <tr key={garage.id}>
                    <td
                      style={{
                        padding: "15px 16px",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {garage.name}
                      <div
                        style={{
                          color: C.muted,
                          fontSize: 11,
                          fontWeight: 400,
                          marginTop: 3,
                        }}
                      >
                        {garage.id}
                      </div>
                    </td>
                    <td style={{ padding: "15px 16px", fontSize: 12 }}>
                      {garage.code}
                    </td>
                    <td style={{ padding: "15px 16px" }}>
                      <Badge
                        tone={garage.status === "Active" ? "green" : "amber"}
                      >
                        {garage.status}
                      </Badge>
                    </td>
                    <td style={{ padding: "15px 16px", fontSize: 12 }}>
                      {garage.subscriptionPlan || "—"}
                    </td>
                    <td style={{ padding: "15px 16px" }}>
                      <Badge
                        tone={
                          garage.subscriptionStatus === "Active"
                            ? "green"
                            : "neutral"
                        }
                      >
                        {garage.subscriptionStatus}
                      </Badge>
                    </td>
                    <td
                      style={{
                        padding: "15px 16px",
                        fontSize: 11,
                        color: C.muted,
                      }}
                    >
                      {garage.mechanicsCount || 0} mechanics ·{" "}
                      {garage.customersCount || 0} customers
                    </td>
                    <td style={{ padding: "15px 16px", whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => setForm({ ...garage, password: "" })}
                        style={linkButton}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setPasswordGarage(garage);
                          setNewPassword("");
                        }}
                        style={linkButton}
                      >
                        Password
                      </button>
                      <button
                        onClick={() => removeGarage(garage)}
                        style={{ ...linkButton, color: C.red }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div
          style={{
            marginTop: 28,
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflowX: "auto",
          }}
        >
          <div
            style={{
              padding: "18px 16px",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 17 }}>Seller contact requests</h2>
            <p style={{ margin: "5px 0 0", color: C.muted, fontSize: 12 }}>
              Buyer details submitted to connect with marketplace sellers.
            </p>
          </div>
          {!contactRequests.length ? (
            <p style={{ padding: 18, color: C.muted, fontSize: 13 }}>
              No contact requests yet.
            </p>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 860,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Buyer",
                    "Phone",
                    "Seller / yard",
                    "Location",
                    "Product ID",
                    "Type",
                    "Received",
                  ].map((heading) => (
                    <th
                      key={heading}
                      style={{
                        textAlign: "left",
                        padding: "12px 16px",
                        borderBottom: `1px solid ${C.border}`,
                        color: C.muted,
                        fontSize: 10,
                        textTransform: "uppercase",
                      }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contactRequests.map((request) => (
                  <tr key={request.id}>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      {request.fullName}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12 }}>
                      {request.phone}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12 }}>
                      {request.seller?.yardName || "—"}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12 }}>
                      {request.seller?.location || "—"}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: 11,
                        fontFamily: "monospace",
                      }}
                    >
                      {(request.listingType === "vehicle" || request.listingType === "hire") ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/inventory/${request.productId || request.listingId}`)}
                          style={buttonStyle("secondary")}
                        >
                          View car
                        </button>
                      ) : (
                        request.productId || request.listingId
                      )}
                    </td>
                    <td style={{ padding: "14px 16px", fontSize: 12 }}>
                      {request.listingType}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: 12,
                        color: C.muted,
                      }}
                    >
                      {new Date(request.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div
          style={{
            marginTop: 28,
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflowX: "auto",
          }}
        >
          <div style={{ padding: "18px 16px", borderBottom: `1px solid ${C.border}` }}>
            <h2 style={{ margin: 0, fontSize: 17 }}>Marketplace users</h2>
            <p style={{ margin: "5px 0 0", color: C.muted, fontSize: 12 }}>
              Accounts registered to buy or sell on the marketplace.
            </p>
          </div>
          {!marketplaceUsers.length ? (
            <p style={{ padding: 18, color: C.muted, fontSize: 13 }}>No marketplace users yet.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead><tr>{["Email", "Role", "Yard / shop", "Phone", "Location", "Joined"].map((heading) => (
                <th key={heading} style={{ textAlign: "left", padding: "12px 16px", borderBottom: `1px solid ${C.border}`, color: C.muted, fontSize: 10, textTransform: "uppercase" }}>{heading}</th>
              ))}</tr></thead>
              <tbody>{marketplaceUsers.map((user) => (
                <tr key={user.id}>
                  <td style={{ padding: "14px 16px", fontSize: 13, fontWeight: 700 }}>{user.email}</td>
                  <td style={{ padding: "14px 16px" }}><Badge tone={user.role === "seller" ? "green" : "neutral"}>{user.role}</Badge></td>
                  <td style={{ padding: "14px 16px", fontSize: 12 }}>{user.yardName || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 12 }}>{user.phone || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 12 }}>{user.location || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 12, color: C.muted }}>{new Date(user.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </section>
      {passwordGarage && (
        <div style={modal}>
          <form onSubmit={changePassword} style={modalCard}>
            <h2 style={{ margin: 0, fontSize: 17 }}>Reset garage password</h2>
            <p style={{ color: C.muted, fontSize: 13 }}>
              Set a new password for {passwordGarage.name}.
            </p>
            <Field label="New password">
              <input
                required
                minLength={8}
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 18,
              }}
            >
              <button
                type="button"
                onClick={() => setPasswordGarage(null)}
                style={buttonStyle("secondary")}
              >
                Cancel
              </button>
              <button disabled={saving} style={buttonStyle("primary")}>
                Reset password
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

const linkButton = {
  border: 0,
  background: "none",
  color: "#2563eb",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 700,
  marginRight: 8,
  padding: 0,
};
const modal = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,.45)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 20,
};
const modalCard = {
  background: "#fff",
  borderRadius: 10,
  padding: 24,
  width: "100%",
  maxWidth: 420,
};
